import { lazy, Suspense } from "react";
import { Navigate, NavLink, useParams } from "react-router";
import { MembersPanel } from "~/common/account/members";
import { AppearancePanel, DensityPanel, LanguagePanel, ProfilePanel } from "~/common/account/panels";
import { SubscriptionPanel } from "~/common/billing/subscription";
import { UsagePanel } from "~/common/billing/usage";
import { GROUPS, SECTIONS, type SettingsSection, settingsEntry } from "~/common/settings/catalog";
import { TrafficSetup } from "~/common/settings/tracking";
import type { Me, Org } from "~/core/api";
import { useT } from "~/core/i18n";
import { settings } from "~/core/paths";
import { isAdmin } from "~/core/session";
import { PageHead } from "~/core/ui/page";
import { ListSkeleton } from "~/core/ui/skeletons";
import { BrandSettings } from "~/modules/analytics/pages/settings";

/** Agent settings load only when their section is opened. */
const Integrations = lazy(() => import("@/client/integrations-section"));
const Secrets = lazy(() => import("@/client/secrets-section"));

/**
 * One settings surface, owned by the shell.
 *
 * The Agent module used to draw it: the route mounted the Agent's whole
 * provider stack — PostHog, the socket, the session — and the Agent's sidebar
 * to render sections that are almost all this tree's, so the page showed the
 * Agent's navigation while the mode switch above it said Analytics. It is a
 * screen of the shell now, under Analytics' navigation where its Settings row
 * already was.
 *
 * The section navigation is the page's own rather than the sidebar's: the
 * sidebar says which brand and which module, and settings is one level below
 * both.
 */
export function SettingsPage({ me, org, brandId }: { me: Me; org: Org; brandId: string }) {
	const t = useT();
	const { section } = useParams();
	const entry = settingsEntry(section);

	// A section this build does not have: a hand-typed path. The page has to
	// open on something, and Profile is what somebody who asked for "settings"
	// and nothing more meant. A brand's section without a brand is the same
	// kind of address: it names a form with nothing behind it.
	if (!entry || (entry.brand && !brandId)) return <Navigate to={settings(brandId, "profile")} replace />;

	const shown = SECTIONS.filter((one) => brandId || !one.brand);

	return (
		<>
			<PageHead icon={<entry.icon />} title={t(entry.label)} within={t("nav.settings")} lede={t(entry.description)} />
			<div className="settings-page">
				<nav className="settings-nav" aria-label={t("settings.nav")}>
					{GROUPS.map((group) =>
						shown.some((one) => one.group === group.id) ? (
							<div key={group.id} className="settings-nav-group">
								<h2>{t(group.title)}</h2>
								{shown
									.filter((one) => one.group === group.id)
									.map(({ id, label, icon: Icon }) => (
										// Links rather than tabs: these are addresses, and somebody
										// who middle-clicks Members to keep their place expects a
										// second tab.
										<NavLink key={id} to={settings(brandId, id)} className="nav-item">
											<Icon aria-hidden />
											<span>{t(label)}</span>
										</NavLink>
									))}
							</div>
						) : null,
					)}
				</nav>
				<div className="settings-body">
					<Suspense fallback={<ListSkeleton />}>
						{/* Keyed on the section, so a body holding a form, a search term
						    or a half-open dialog is torn down when the reader moves on
						    rather than handing its state to the next one. */}
						<Body key={entry.id} section={entry.id} me={me} org={org} brandId={brandId} />
					</Suspense>
				</div>
			</div>
		</>
	);
}

function Body({ section, me, org, brandId }: { section: SettingsSection; me: Me; org: Org; brandId: string }) {
	switch (section) {
		case "tracking":
			return <TrafficSetup entityId={brandId} />;
		case "profile":
			return <ProfilePanel me={me} org={org} />;
		case "appearance":
			return <AppearancePanel />;
		case "language":
			return <LanguagePanel />;
		case "density":
			return <DensityPanel />;
		case "members":
			return <MembersPanel me={me} org={org} />;
		case "subscription":
			// The plan is the account's to change — an owner's or an admin's, not
			// a brand editor's — so this is not the brand's access context.
			return <SubscriptionPanel org={org} admin={isAdmin(me, org)} />;
		case "usage":
			return <UsagePanel org={org} />;
		case "integrations":
			return <Integrations />;
		case "secrets":
			return <Secrets />;
		default:
			return <BrandSettings entityId={brandId} section={section} />;
	}
}
